<template>
  <r-ai-page-container>
    <!-- PR-FREE-WINS-CLARITY: capturing Small Wins is FREE for everyone, so
         this page no longer mounts a premium prompt. Any Plus prompt app-wide
         is handled by the one shared UpgradeToPlusModal (App.vue). -->
    <v-row class="text-start">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-2 fade-in">
          <h1 class="headline-text mb-0">What's New?</h1>
        </div>
        <p class="text-body-1 text--secondary mb-4 fade-in">
          Capture wins, achievements, or notes. Quick and easy — just jot it down.
        </p>

        <v-card class="r-card pa-4 mb-4 fade-in-delay-1">
          <div class="text-subtitle-2 font-weight-medium mb-2">Quick Note</div>
          <p class="text-caption text--secondary mb-3">
            Jot down a quick note or achievement. You can come back and expand it later.
          </p>
          <voice-input
            v-model="rawInput"
            :multiline="true"
            :rows="3"
            label="What happened?"
            placeholder="e.g., 'Closed the Acme deal' or 'Finished the Q3 report ahead of schedule'"
            hint="Type or use the mic to capture a quick note"
          ></voice-input>

          <div v-if="hasJobs" class="mt-3">
            <div class="text-caption text--secondary mb-2">
              <v-icon small class="mr-1">mdi-link-variant</v-icon>
              Link to a role (optional):
            </div>
            <div class="d-flex flex-wrap">
              <v-chip
                v-for="job in jobOptions"
                :key="job.key"
                :color="selectedJobKey === job.key ? 'primary' : 'grey lighten-3'"
                :text-color="selectedJobKey === job.key ? 'white' : 'grey darken-2'"
                small
                class="mr-2 mb-2"
                @click="selectJob(job.key)"
              >
                <v-icon v-if="job.isCurrent" x-small left>mdi-briefcase</v-icon>
                {{ job.label }}
                <v-icon v-if="selectedJobKey === job.key" x-small right>mdi-check</v-icon>
              </v-chip>
              <v-chip
                v-if="selectedJobKey"
                color="grey lighten-2"
                text-color="grey darken-1"
                small
                class="mr-2 mb-2"
                @click="selectedJobKey = null"
              >
                <v-icon x-small left>mdi-close</v-icon>
                Clear
              </v-chip>
            </div>
          </div>

          <div class="d-flex justify-end align-center mt-4">
            <v-btn
              color="primary"
              :loading="parsing"
              :disabled="!rawInput.trim()"
              @click="parseAndSave"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Note
            </v-btn>
          </div>
        </v-card>

        <v-card v-if="isPremium || companyViewEnabled" class="r-card-clickable pa-3 mb-4 fade-in-delay-2" outlined @click="goToGuided" style="border-color: #5c6bc0;">
          <div class="d-flex align-center">
            <v-icon color="indigo" class="mr-3">mdi-message-text-outline</v-icon>
            <div class="flex-grow-1">
              <div class="text-body-2 font-weight-medium">Want Sage to help capture a win?</div>
              <div class="text-caption text--secondary">Have a quick conversation and Sage will build the full story for you.</div>
            </div>
            <v-icon small color="grey">mdi-chevron-right</v-icon>
          </div>
        </v-card>

        <v-card v-if="justSavedNote && (isPremium || companyViewEnabled)" class="pa-4 mb-4 sage-prompt-card" outlined>
          <div class="d-flex align-center">
            <div class="sage-cta-avatar mr-3">
              <span class="sage-avatar-text">S</span>
            </div>
            <div class="flex-grow-1">
              <div class="text-subtitle-2 font-weight-medium">Want to make this stronger?</div>
              <div class="text-caption text--secondary">Sage can help you add context and impact to turn this into a complete achievement.</div>
            </div>
          </div>
          <div class="d-flex justify-end mt-3">
            <v-btn text small @click="justSavedNote = false" class="mr-2">
              Keep as Note
            </v-btn>
            <v-btn color="primary" small @click="expandLastSavedToGuided">
              <v-icon left small>mdi-message-text</v-icon>
              Go Deeper with Sage
            </v-btn>
          </div>
        </v-card>

        <div v-if="recentWins.length > 0">
          <div class="d-flex justify-space-between align-center mb-3">
            <h2 class="section-title mb-0">Recent Captures</h2>
            <v-btn
              text
              small
              color="primary"
              @click="$router.push('/SmallWinsProjects')"
            >
              View all {{ recentWins.length }} entries
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
          <v-card
            v-for="win in displayedWins"
            :key="win.id"
            class="r-card mb-3 pa-4"
          >
            <div class="d-flex align-start">
              <v-chip
                :color="getStatusColor(win.status)"
                small
                class="mr-3"
              >
                {{ getStatusLabel(win.status) }}
              </v-chip>
              <v-chip
                :color="getTypeColor(win.parsedType)"
                small
                outlined
                class="mr-3"
              >
                {{ getTypeLabel(win.parsedType) }}
              </v-chip>
              <v-chip
                v-if="win.linkedTo && getLinkedJobLabel(win.linkedTo)"
                color="teal lighten-4"
                text-color="teal darken-3"
                small
                class="mr-3"
              >
                <v-icon x-small left>mdi-link</v-icon>
                {{ getLinkedJobLabel(win.linkedTo) }}
              </v-chip>
              <div class="flex-grow-1">
                <div class="text-body-1 font-weight-medium">
                  {{ (win.aiDraft && win.aiDraft.summary) || win.rawInput }}
                </div>
                <div class="text-caption text--secondary mt-1">
                  {{ formatDate(win.createdAt) }}
                </div>
              </div>
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="expandWinToGuided(win)">
                    <v-list-item-icon><v-icon>mdi-message-text</v-icon></v-list-item-icon>
                    <v-list-item-title>Go Deeper with Sage</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="editWin(win)">
                    <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="verifyWin(win)" v-if="win.status === 'inferred'">
                    <v-list-item-icon><v-icon>mdi-check</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark as Verified</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteWin(win)">
                    <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div v-if="win.parsedType === 'achievement' && win.aiDraft" class="mt-3 pl-4 star-border">
              <div class="text-caption text--secondary mb-1">STAR Format:</div>
              <div v-if="win.aiDraft.situation" class="text-body-2 mb-1">
                <strong>Situation:</strong> {{ win.aiDraft.situation }}
              </div>
              <div v-if="win.aiDraft.task" class="text-body-2 mb-1">
                <strong>Task:</strong> {{ win.aiDraft.task }}
              </div>
              <div v-if="win.aiDraft.action" class="text-body-2 mb-1">
                <strong>Action:</strong> {{ win.aiDraft.action }}
              </div>
              <div v-if="win.aiDraft.result" class="text-body-2">
                <strong>Result:</strong> {{ win.aiDraft.result }}
              </div>
            </div>

            <div class="mt-2 d-flex align-center">
              <v-btn
                v-if="win.status === 'inferred'"
                x-small
                outlined
                color="success"
                class="mr-2"
                @click="verifyWin(win)"
              >
                <v-icon x-small left>mdi-check-circle</v-icon>
                Verify
              </v-btn>
              <v-btn
                v-if="win.parsedType === 'note' || (win.parsedType === 'achievement' && !hasFullStar(win))"
                x-small
                text
                color="primary"
                @click="expandWinToGuided(win)"
              >
                <v-icon x-small left>mdi-message-text</v-icon>
                Go Deeper with Sage
              </v-btn>
            </div>
          </v-card>

          <div v-if="recentWins.length > displayedWins.length" class="text-center mt-2 mb-4">
            <v-btn
              text
              color="primary"
              @click="$router.push('/SmallWinsProjects')"
            >
              {{ recentWins.length - displayedWins.length }} more — view all & make edits
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-else-if="!loading" class="empty-state">
          <v-icon class="empty-state-icon">mdi-trophy-outline</v-icon>
          <div class="empty-state-title">No captures yet</div>
          <div class="empty-state-subtitle">Talk to Sage or jot a quick note to start building your career evidence.</div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="text-body-2 text--secondary mt-4">Loading your captures...</p>
        </div>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </r-ai-page-container>
</template>

<script>
import VoiceInput from "../../components/VoiceInput.vue";
import * as SmallWinsApi from "../../api/SmallWinsApi";
import * as ProfileContextApi from "../../api/ProfileContextApi";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";

export default {
  name: "SmallWinsCapture",
  components: {
    "voice-input": VoiceInput,
  },
  data: function() {
    return {
      rawInput: "",
      recentWins: [],
      loading: false,
      parsing: false,
      justSavedNote: false,
      lastSavedText: "",
      lastSavedId: null,
      selectedJobKey: null,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success"
    };
  },
  mounted: function() {
    this.loadWins();
  },
  methods: {
    normalizeWin: function(item) {
      if (!item.rawInput && item.description) item.rawInput = item.description;
      if (!item.description && item.rawInput) item.description = item.rawInput;
      if (!item.parsedType && item.category) item.parsedType = item.category;
      if (!item.category && item.parsedType) item.category = item.parsedType;
      if (!item.status) item.status = "inferred";
      if (!item.linkedTo && item.assignedToLabel) item.linkedTo = item.assignedToLabel;
      if (!item.aiDraft) {
        var hasStar = item.context || item.task || item.action || item.outcome;
        if (hasStar || item.rawInput || item.description) {
          item.aiDraft = {
            summary: item.rawInput || item.description || "",
            situation: item.context || "",
            task: item.task || "",
            action: item.action || "",
            result: item.outcome || ""
          };
        }
      }
      if (!item.id) {
        item.id = "sw-" + Date.now() + "-" + Math.random().toString(36).substring(2, 8);
      }
      if (item.aiDraft && !item.context && item.aiDraft.situation) item.context = item.aiDraft.situation;
      if (item.aiDraft && !item.outcome && item.aiDraft.result) item.outcome = item.aiDraft.result;
      if (!item.createdAt && item.eventDate) item.createdAt = item.eventDate;
      if (!item.eventDate && item.createdAt) item.eventDate = item.createdAt;
      return item;
    },
    loadWins: function() {
      var self = this;
      this.loading = true;
      SmallWinsApi.GetSmallWins().then(function(result) {
        if (result.success) {
          var data = result.data;
          var items = [];
          if (Array.isArray(data)) {
            items = data;
          } else if (data && Array.isArray(data.items)) {
            items = data.items;
          }
          items = items.map(function(item) { return self.normalizeWin(item); });
          items.sort(function(a, b) {
            var da = a.createdAt || a.eventDate || "";
            var db = b.createdAt || b.eventDate || "";
            return da > db ? -1 : da < db ? 1 : 0;
          });
          self.recentWins = items;
        }
      }).catch(function(e) {
        console.error("Failed to load wins:", e);
      }).finally(function() {
        self.loading = false;
      });
    },
    parseAndSave: function() {
      if (!this.rawInput.trim()) return;
      var self = this;
      this.parsing = true;
      var savedText = this.rawInput.trim();

      SmallWinsApi.CreateSmallWin(
        this.rawInput,
        "note",
        { summary: this.rawInput },
        "inferred",
        this.selectedJobKey
      ).then(function(result) {
        if (result.success) {
          self.showSnackbar("Note saved!", "success");
          self.lastSavedText = savedText;
          self.lastSavedId = null;
          self.rawInput = "";
          self.selectedJobKey = null;
          self.justSavedNote = true;
          ProfileContextApi.ClearProfileContextCache();
          // PR-GO-DEEPER-PARITY: CreateSmallWin generates the id client-side and
          // does NOT return it, so read it back from the reloaded store list -
          // the newest item whose rawInput matches the note we just saved. This
          // lets "Want to make this stronger?" edit the SAME record (no dupe).
          self.$store
            .dispatch("smallWins/loadSmallWins")
            .then(function() {
              var items = self.$store.state.smallWins.items || [];
              var match = null;
              for (var i = 0; i < items.length; i++) {
                var w = items[i];
                var wText = w.rawInput || w.description || "";
                if (wText !== savedText) continue;
                if (
                  !match ||
                  (w.createdAt || w.eventDate || "") >
                    (match.createdAt || match.eventDate || "")
                ) {
                  match = w;
                }
              }
              if (match && match.id) {
                self.lastSavedId = match.id;
              }
            })
            .catch(function() {
              /* id lookup is best-effort; fall back to text-only deepen */
            });
          self.loadWins();
        } else {
          self.showSnackbar("Failed to save: " + (result.msg || "Unknown error"), "error");
        }
      }).catch(function(e) {
        console.error("Save error:", e);
        self.showSnackbar("Error saving note", "error");
      }).finally(function() {
        self.parsing = false;
      });
    },
    goToGuided: function() {
      this.$router.push("/Sage");
    },
    expandLastSavedToGuided: function() {
      // PR-GO-DEEPER-PARITY: pass existingWinId (when we captured it) so Sage
      // refines the just-saved record in place instead of creating a duplicate.
      var query = { accomplishment: this.lastSavedText };
      if (this.lastSavedId) {
        query.existingWinId = this.lastSavedId;
      }
      this.$router.push({
        path: "/Sage",
        query: query
      });
    },
    expandWinToGuided: function(win) {
      var text = (win.aiDraft && win.aiDraft.summary) || win.rawInput || "";
      var query = { accomplishment: text, existingWinId: win.id };
      if (win.aiDraft) {
        if (win.aiDraft.situation) query.situation = win.aiDraft.situation;
        if (win.aiDraft.task) query.task = win.aiDraft.task;
        if (win.aiDraft.action) query.action = win.aiDraft.action;
        if (win.aiDraft.result) query.result = win.aiDraft.result;
      }
      if (win.rawInput && win.rawInput !== text) {
        query.rawInput = win.rawInput;
      }
      if (win.linkedTo) {
        query.linkedTo = win.linkedTo;
      }
      this.$router.push({ path: "/Sage", query: query });
    },
    hasFullStar: function(win) {
      if (!win.aiDraft) return false;
      return win.aiDraft.situation && win.aiDraft.task && win.aiDraft.action && win.aiDraft.result;
    },
    // PR-WIN-EDIT-PARITY: route to the canonical Small Wins editor so the
    // What's New? "Edit" action opens the SAME dialog as My Wins (full STAR
    // + Date + Linked role + Tags), instead of the limited inline subset.
    // SmallWinsProjects auto-opens its edit dialog when ?edit=<id> is present.
    editWin: function(win) {
      if (!win || !win.id) return;
      this.$router.push({
        path: "/SmallWinsProjects",
        query: { edit: win.id }
      });
    },
    verifyWin: function(win) {
      var self = this;
      win.status = "verified";
      SmallWinsApi.UpdateSmallWin(win.id, { status: "verified" }, win).then(function(result) {
        if (result.success) {
          self.showSnackbar("Marked as verified!", "success");
          self.$store.dispatch("smallWins/loadSmallWins");
          self.loadWins();
        } else {
          win.status = "inferred";
          self.showSnackbar("Failed to verify — please try again.", "error");
        }
      }).catch(function() {
        win.status = "inferred";
        self.showSnackbar("Failed to verify — please try again.", "error");
      });
    },
    deleteWin: function(win) {
      if (!confirm("Delete this capture?")) return;
      var self = this;
      SmallWinsApi.DeleteSmallWin(win.id, win).then(function(result) {
        if (result.success) {
          self.showSnackbar("Deleted", "info");
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
          self.loadWins();
        }
      });
    },
    showSnackbar: function(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
    getStatusColor: function(status) {
      var colors = { inferred: "warning", reviewed: "info", verified: "success" };
      return colors[status] || "grey";
    },
    getStatusLabel: function(status) {
      var labels = { inferred: "Needs Review", reviewed: "Reviewed", verified: "Verified" };
      return labels[status] || status;
    },
    getTypeColor: function(type) {
      var colors = { achievement: "primary", skill_signal: "success", note: "grey" };
      return colors[type] || "grey";
    },
    getTypeLabel: function(type) {
      var labels = { achievement: "Achievement", skill_signal: "Skill", note: "Note" };
      return labels[type] || type;
    },
    formatDate: function(dateStr) {
      if (!dateStr) return "";
      var date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit"
      });
    },
    selectJob: function(jobKey) {
      this.selectedJobKey = this.selectedJobKey === jobKey ? null : jobKey;
    },
    getJobLabel: function(job) {
      var title = (job.workRole && job.workRole.positions && job.workRole.positions[0] && job.workRole.positions[0].title) || (job.workGeneral && job.workGeneral.display) || "Role";
      var company = (job.workGeneral && job.workGeneral.companyName) || "";
      return company ? title + " @ " + company : title;
    },
    getLinkedJobLabel: function(linkedTo) {
      if (!linkedTo || !this.workDict || !this.workDict[linkedTo]) return null;
      return this.getJobLabel(this.workDict[linkedTo]);
    }
  },
  computed: {
    ...mapState("work", { workDict: "workDict" }),
    ...mapState("settings", ["companyViewEnabled"]),
    ...mapFields("auth", { isPremium: "isPremium" }),
    jobOptions: function() {
      var self = this;
      var jobs = [];
      if (this.workDict) {
        for (var key in this.workDict) {
          var job = this.workDict[key];
          if (job && job.workGeneral) {
            jobs.push({
              key: key,
              label: self.getJobLabel(job),
              isCurrent: job.workGeneral.workingCurrently || false
            });
          }
        }
      }
      jobs.sort(function(a, b) { return (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0); });
      return jobs;
    },
    hasJobs: function() {
      return this.jobOptions.length > 0;
    },
    displayedWins: function() {
      return this.recentWins.slice(0, 10);
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}

.sage-cta-card {
  border: 2px solid var(--v-primary-base, #1E3A5F) !important;
  transition: background-color 0.2s;
}

.sage-cta-card:hover {
  background-color: rgba(30, 58, 95, 0.08);
}

.sage-cta-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--v-primary-base, #1E3A5F);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sage-avatar-text {
  color: white;
  font-weight: 700;
  font-size: 18px;
  line-height: 1;
}

.sage-prompt-card {
  border-left: 4px solid var(--v-primary-base, #1E3A5F) !important;
  background: rgba(30, 58, 95, 0.04);
}

.star-border {
  border-left: 3px solid var(--v-primary-base, #1976d2);
}

.promote-preview {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
}
</style>

<style>
.theme--dark .sage-cta-card:hover {
  background-color: rgba(144, 202, 249, 0.08);
}

.theme--dark .sage-prompt-card {
  background: rgba(144, 202, 249, 0.06);
}

.theme--dark .promote-preview {
  background: rgba(255, 255, 255, 0.06);
}
</style>
